import { ApolloError } from '@apollo/client';

const DEFAULT_ERROR_MESSAGE = 'Došlo je do neočekivane greške. Molimo pokušajte ponovo.';

/**
 * Extract error message from various error sources
 */
const extractErrorMessage = (error: ApolloError): string | undefined => {
  // Try to get message from GraphQL error
  const graphQLError = error.graphQLErrors?.[0];
  if (graphQLError?.message) {
    return graphQLError.message;
  }

  // Try network error
  if (error.networkError) {
    return 'Greška u mreži. Provjerite internetsku vezu.';
  }

  return error.message;
};

/**
 * Handle Apollo/GraphQL errors and return user-friendly message
 * 
 * @param error - Apollo error from mutation/query
 * @returns User-friendly error message in Croatian
 * 
 * @example
 * try {
 *   await createSoMutation({ variables: { input } });
 * } catch (error) {
 *   const message = handleErrorMessage(error as ApolloError);
 *   showToast(message);
 * }
 */
export const handleErrorMessage = (error: ApolloError | Error | unknown): string => {
  if (!error) {
    return DEFAULT_ERROR_MESSAGE;
  }

  // Handle ApolloError
  if (error instanceof ApolloError || (error as ApolloError).graphQLErrors) {
    const apolloError = error as ApolloError;

    // Fall back to extracted message
    const extractedMessage = extractErrorMessage(apolloError);
    if (extractedMessage) {
      return extractedMessage;
    }
  }

  // Handle generic Error
  if (error instanceof Error) {
    return error.message || DEFAULT_ERROR_MESSAGE;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  return DEFAULT_ERROR_MESSAGE;
};
